/**
 * @file 教材 1 冊の編集 (状態・使う時期・単元・ページ・固定のメモ・商品ページ・メモの履歴)。
 *
 * @what: 教材タブのカードの中身と、見取り図から開く教材の対話で同じものを使う。
 *   変更は onChange で draft に返し、保存バーで PUT される。メモの履歴は取り組みの記録。
 * @why: 同じ編集 UI を 2 か所に持たない (代表 2026-08-24: 見取り図の教本名をタップして対話で直す)。
 * @failure: 描画と draft の更新のみ。記録の追加失敗は文言を出す。
 */

import { useState } from "react";
import { formatLogDate, todayLocal } from "../api/dates";
import {
  STUDY_MATERIAL_STATUS_LABELS,
  isRunningLow,
  newProgressLogId,
  nextMaterialStatus,
  remainingPages,
  type StudyMaterialInput,
  type StudyProgressLog,
  type StudyProgressLogInput,
} from "../api/plans";
import { unitsInCell, type AttainmentModel, type Stage } from "../model";
import "./study.css";

export interface MaterialEditorProps {
  model: AttainmentModel;
  material: StudyMaterialInput;
  onChange: (next: StudyMaterialInput) => void;
  onRemove: () => void;
  /** 「単元を選ぶ」に出す単元のステップ。 */
  pickerStage: Stage;
  /** 取り組みの記録 (この教材のぶんだけ)。 */
  logs: StudyProgressLog[];
  onAddLog?: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  logBusy: boolean;
  logError: string | null;
}

export function MaterialEditor({
  model,
  material: m,
  onChange,
  onRemove,
  pickerStage,
  logs,
  onAddLog,
  logBusy,
  logError,
}: MaterialEditorProps) {
  const unitLabelById = new Map(model.units.map((u) => [u.id, u.label]));
  return (
    <div className="study-material-card-body">
      <div className="study-material-card-controls">
        <button
          type="button"
          className={`study-material-status study-material-status--${m.status}`}
          onClick={() => onChange({ ...m, status: nextMaterialStatus(m.status) })}
          aria-label={`${m.title}（${STUDY_MATERIAL_STATUS_LABELS[m.status]}）`}
          title="クリックで 検討中 → 用意した → 使っている → 使い終えた の順に切り替わります"
        >
          {STUDY_MATERIAL_STATUS_LABELS[m.status]}
        </button>
        <label className="study-material-stage">
          使う時期{" "}
          <select
            aria-label={`${m.title} を使うステップ`}
            value={m.stage_id ?? ""}
            onChange={(e) => {
              const next = { ...m };
              if (e.target.value) {
                next.stage_id = e.target.value;
              } else {
                delete next.stage_id;
              }
              onChange(next);
            }}
          >
            <option value="">決めない</option>
            {model.stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.period}
              </option>
            ))}
          </select>
        </label>
        {m.url ? (
          <a className="study-material-buy" href={m.url} target="_blank" rel="noopener noreferrer">
            商品ページ
          </a>
        ) : null}
        {m.status === "excluded" ? (
          <span className="study-material-excluded-hint">見取り図には出ません</span>
        ) : (
          <button
            type="button"
            className="study-link-btn study-material-exclude"
            onClick={() => onChange({ ...m, status: "excluded" })}
          >
            候補から外す
          </button>
        )}
      </div>
      <div className="study-material-units">
        {m.unit_ids.length > 0 ? (
          m.unit_ids.map((id) => (
            <span key={id} className="study-material-unit-chip">
              {unitLabelById.get(id) ?? id}
            </span>
          ))
        ) : (
          <span className="study-muted study-material-units-empty">単元を決めていません</span>
        )}
      </div>
      <details className="study-unit-picker">
        <summary>単元を選ぶ（{pickerStage.period}）</summary>
        <div className="study-unit-picker-groups">
          {model.domains.map((d) => {
            const units = unitsInCell(model, pickerStage.id, d.id);
            if (units.length === 0) return null;
            return (
              <div key={d.id} className="study-unit-picker-group">
                <div className="study-prep-group-title">{d.label}</div>
                {units.map((u) => {
                  const checked = m.unit_ids.includes(u.id);
                  return (
                    <label key={u.id} className="study-unit-picker-item">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          onChange({
                            ...m,
                            unit_ids: checked
                              ? m.unit_ids.filter((id) => id !== u.id)
                              : [...m.unit_ids, u.id].sort(),
                          })
                        }
                      />
                      <span>{u.label}</span>
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      </details>
      <div className="study-material-pages">
        <label>
          全{" "}
          <input
            type="number"
            min={0}
            max={9999}
            aria-label={`${m.title} の総ページ数`}
            value={m.total_pages ?? ""}
            onChange={(e) => {
              const v = Number(e.target.value);
              const next = { ...m };
              if (e.target.value === "" || !Number.isFinite(v) || v <= 0) {
                delete next.total_pages;
              } else {
                next.total_pages = Math.floor(v);
              }
              onChange(next);
            }}
          />{" "}
          ページ
        </label>
        <label>
          いま{" "}
          <input
            type="number"
            min={0}
            max={9999}
            aria-label={`${m.title} のいまのページ`}
            value={m.current_page ?? ""}
            onChange={(e) => {
              const v = Number(e.target.value);
              const next = { ...m };
              if (e.target.value === "" || !Number.isFinite(v) || v <= 0) {
                delete next.current_page;
              } else {
                next.current_page = Math.floor(v);
              }
              onChange(next);
            }}
          />{" "}
          ページ
        </label>
        {remainingPages(m) !== null ? <span>残り {remainingPages(m)} ページ</span> : null}
        {isRunningLow(m) ? (
          <span className="study-low-chip">残りわずか。次の教材を考え始めましょう</span>
        ) : null}
      </div>
      <MaterialNotes material={m} logs={logs} {...(onAddLog ? { onAddLog } : {})} busy={logBusy} error={logError} />
      <div className="study-material-card-foot">
        <input
          className="study-material-memo"
          type="text"
          placeholder="固定のメモ（対象年齢・使い方など）"
          aria-label={`${m.title} のメモ`}
          value={m.memo ?? ""}
          maxLength={500}
          onChange={(e) => {
            const memo = e.target.value;
            const next = { ...m };
            if (memo) {
              next.memo = memo;
            } else {
              delete next.memo;
            }
            onChange(next);
          }}
        />
        <button
          type="button"
          className="study-material-del"
          aria-label={`${m.title} を削除`}
          onClick={onRemove}
        >
          削除
        </button>
      </div>
    </div>
  );
}

/** 教材ごとのメモの履歴 (取り組みの記録を日付つきで) と「メモを足す」。 */
export function MaterialNotes({
  material,
  logs,
  onAddLog,
  busy,
  error,
}: {
  material: StudyMaterialInput;
  logs: StudyProgressLog[];
  onAddLog?: (input: StudyProgressLogInput) => Promise<StudyProgressLog | null>;
  busy: boolean;
  error: string | null;
}) {
  const [text, setText] = useState("");
  const [showAll, setShowAll] = useState(false);
  // 失敗の文言は、このカードで足そうとしたときだけ出す (hook の文言は学習者単位で 1 つのため)
  const [attempted, setAttempted] = useState(false);
  const shown = showAll ? logs : logs.slice(0, 3);
  const submit = async () => {
    const memo = text.trim();
    if (!memo || !onAddLog || busy) return;
    setAttempted(true);
    const created = await onAddLog({
      id: newProgressLogId(),
      date: todayLocal(),
      material_id: material.id,
      memo,
    });
    if (created) {
      setText("");
      setAttempted(false);
    }
  };
  return (
    <div className="study-material-notes">
      {logs.length > 0 ? (
        <ul className="study-material-note-list" aria-label={`${material.title} のメモの履歴`}>
          {shown.map((l) => (
            <li key={l.id}>
              <span className="study-material-note-date">{formatLogDate(l.date)}</span>
              <span className="study-material-note-text">
                {l.memo}
                {l.to_page ? (
                  <span className="study-material-note-pages">
                    {l.from_page ? `p.${l.from_page}〜${l.to_page}` : `p.${l.to_page}`}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {logs.length > 3 ? (
        <button type="button" className="study-link-btn" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "最近の 3 件だけ見る" : `すべて見る（${logs.length} 件）`}
        </button>
      ) : null}
      {onAddLog ? (
        <div className="study-material-note-add">
          <input
            type="text"
            placeholder="メモを足す（今日の日付で記録に残ります）"
            aria-label={`${material.title} にメモを足す`}
            value={text}
            maxLength={500}
            onChange={(e) => setText(e.target.value)}
          />
          <button
            type="button"
            className="study-ghost-btn study-ghost-btn--sm"
            onClick={() => void submit()}
            disabled={!text.trim() || busy}
          >
            足す
          </button>
        </div>
      ) : null}
      {attempted && error ? (
        <p className="study-material-lookup-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
